import { Link } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import SEOHead from '../../../components/base/SEOHead';

export default function GrantOpportunitiesPage() {
  const federalGrants = [
    {
      title: 'Small Business Innovation Research (SBIR)',
      agency: 'Multiple Federal Agencies',
      amount: 'Phase I: Up to $250,000 | Phase II: Up to $1 million',
      description: 'Supports research and development projects with commercial potential in technology and innovation.',
      eligibility: 'Small businesses with fewer than 500 employees engaged in R&D'
    },
    {
      title: 'Small Business Technology Transfer (STTR)',
      agency: 'Multiple Federal Agencies',
      amount: 'Phase I: Up to $250,000 | Phase II: Up to $1 million',
      description: 'Similar to SBIR but requires partnership with a research institution.',
      eligibility: 'Small businesses partnering with nonprofit research institutions'
    },
    {
      title: 'Economic Development Administration (EDA) Grants',
      agency: 'U.S. Department of Commerce',
      amount: 'Varies by program',
      description: 'Supports economic development projects that create jobs and promote innovation.',
      eligibility: 'Businesses in economically distressed areas, including parts of El Paso'
    }
  ];

  const stateGrants = [
    {
      title: 'Texas Enterprise Fund',
      description: 'Incentive program for businesses creating jobs in Texas',
      amount: 'Negotiable based on project',
      focus: 'Job creation, capital investment, economic development'
    },
    {
      title: 'Skills Development Fund',
      description: 'Training grants for businesses to upgrade workforce skills',
      amount: 'Up to $500,000 per project',
      focus: 'Employee training and workforce development'
    },
    {
      title: 'Texas Product Development Fund',
      description: 'Supports commercialization of innovative products',
      amount: 'Up to $250,000',
      focus: 'Product development, technology commercialization'
    }
  ];

  const specialtyGrants = [
    {
      category: 'Women-Owned Businesses',
      programs: [
        'Amber Grant - $10,000 monthly grants for women entrepreneurs',
        'Cartier Women\'s Initiative - Up to $100,000 for women-led businesses',
        'IFundWomen Grant - Various amounts through crowdfunding platform',
        'Tory Burch Foundation Fellowship - $5,000 grant plus education'
      ]
    },
    {
      category: 'Minority-Owned Businesses',
      programs: [
        'NASE Growth Grants - Up to $4,000 for small business growth',
        'Minority Business Development Agency (MBDA) - Various programs',
        'National Association for Latino Community Asset Builders - Multiple grant programs',
        'Hispanic Chamber of Commerce Grants - Local and national opportunities'
      ]
    },
    {
      category: 'Veteran-Owned Businesses',
      programs: [
        'StreetShares Foundation Veteran Small Business Award - $5,000 monthly',
        'FedEx Small Business Grant Contest - Up to $50,000',
        'Veteran Business Outreach Center Grants - Various amounts',
        'Boots to Business Program - Training and funding opportunities'
      ]
    }
  ];

  const applicationTimeline = [
    {
      phase: 'Research Phase',
      duration: '2-4 weeks',
      tasks: ['Identify relevant grants', 'Review eligibility requirements', 'Gather required documentation', 'Study past successful applications']
    },
    {
      phase: 'Preparation Phase',
      duration: '4-6 weeks',
      tasks: ['Write compelling narrative', 'Develop detailed budget', 'Collect supporting documents', 'Get letters of support']
    },
    {
      phase: 'Application Phase',
      duration: '1-2 weeks',
      tasks: ['Complete application forms', 'Review and edit thoroughly', 'Submit before deadline', 'Confirm receipt']
    },
    {
      phase: 'Follow-Up Phase',
      duration: '2-6 months',
      tasks: ['Wait for review decision', 'Respond to any questions', 'Accept award if approved', 'Begin grant reporting']
    }
  ];

  const successTips = [
    {
      icon: 'ri-target-line',
      title: 'Align with Grant Goals',
      description: 'Ensure your project directly addresses the grant\'s objectives and priorities. Tailor your application to match their mission.'
    },
    {
      icon: 'ri-article-line',
      title: 'Tell a Compelling Story',
      description: 'Create a narrative that shows the impact of your business on the El Paso community and why funding is crucial.'
    },
    {
      icon: 'ri-calculator-line',
      title: 'Provide Detailed Budget',
      description: 'Break down exactly how grant funds will be used. Be specific, realistic, and justify every expense.'
    },
    {
      icon: 'ri-file-list-3-line',
      title: 'Follow Instructions Exactly',
      description: 'Read guidelines carefully and follow formatting, length, and submission requirements precisely.'
    },
    {
      icon: 'ri-team-line',
      title: 'Demonstrate Capacity',
      description: 'Show your team has the skills and experience to successfully execute the project and manage grant funds.'
    },
    {
      icon: 'ri-time-line',
      title: 'Start Early',
      description: 'Begin the application process well before the deadline. Quality applications take time to develop.'
    }
  ];

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://elpasotexasbusiness.com/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Resources",
        "item": "https://elpasotexasbusiness.com/resources"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": "Grant Opportunities",
        "item": "https://elpasotexasbusiness.com/resources/grant-opportunities"
      }
    ]
  };

  return (
    <>
      <SEOHead
        title="Business Grants for El Paso Entrepreneurs - Federal, State & Local"
        description="Discover business grant opportunities for El Paso startups and small businesses. Find federal, Texas state, and local grants for women, minorities, veterans, and tech entrepreneurs."
        canonical="https://elpasotexasbusiness.com/resources/grant-opportunities"
        ogImage="https://public.readdy.ai/ai/img_res/ec54dbc42128c2e3abd872c8b2a82b01.jpg"
      />
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbSchema)}
      </script>
      
      <div className="min-h-screen bg-white flex flex-col">
        <Navbar />
        
        <main className="flex-grow">
          {/* Breadcrumb */}
          <nav aria-label="Breadcrumb" className="bg-gray-50 border-b border-gray-200">
            <div className="max-w-7xl mx-auto px-6">
              <div className="mb-8">
                <Link to="/resources" className="text-[#D2691E] hover:text-[#B8571A] font-semibold flex items-center space-x-2 transition-colors whitespace-nowrap">
                  <i className="ri-arrow-left-line"></i>
                  <span>Back to Resources</span>
                </Link>
              </div>
            </div>
          </nav>

          <div className="max-w-7xl mx-auto px-6">
            <div className="mb-16">
              <h1 className="text-6xl font-bold text-gray-900 mb-6">Grant Opportunities</h1>
              <p className="text-xl text-gray-600 leading-relaxed max-w-4xl">
                Discover federal, state, and private grant programs available to El Paso entrepreneurs. 
                Unlike loans, grants don't need to be repaid, making them an excellent funding option for qualifying businesses.
              </p>
            </div>

            <section className="mb-20">
              <h2 className="text-4xl font-bold text-gray-900 mb-12">Federal Grant Programs</h2>
              <div className="space-y-6">
                {federalGrants.map((grant, index) => (
                  <div key={index} className="bg-[#FAFAF8] rounded-2xl p-10">
                    <div className="flex items-start justify-between mb-4">
                      <h3 className="text-2xl font-bold text-gray-900">{grant.title}</h3>
                      <span className="bg-[#D2691E] text-white px-4 py-2 rounded-full text-xs font-semibold whitespace-nowrap">
                        {grant.agency}
                      </span>
                    </div>
                    <div className="mb-4">
                      <span className="text-[#D2691E] font-bold text-lg">{grant.amount}</span>
                    </div>
                    <p className="text-base text-gray-600 mb-6 leading-relaxed">{grant.description}</p>
                    <div className="bg-white rounded-xl p-6">
                      <h4 className="font-bold text-gray-900 mb-2 text-base">Eligibility</h4>
                      <p className="text-sm text-gray-700">{grant.eligibility}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-20">
              <h2 className="text-4xl font-bold text-gray-900 mb-12">Texas State Grants</h2>
              <div className="grid md:grid-cols-3 gap-8">
                {stateGrants.map((grant, index) => (
                  <div key={index} className="bg-[#FAFAF8] rounded-2xl p-8">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{grant.title}</h3>
                    <p className="text-sm text-gray-600 mb-4 leading-relaxed">{grant.description}</p>
                    <div className="mb-4">
                      <span className="text-[#D2691E] font-bold text-base">{grant.amount}</span>
                    </div>
                    <div className="pt-4 border-t border-gray-200">
                      <p className="text-xs text-gray-500 font-semibold mb-2">Focus Areas</p>
                      <p className="text-sm text-gray-700">{grant.focus}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-20">
              <h2 className="text-4xl font-bold text-gray-900 mb-12">Specialty Grant Programs</h2>
              <div className="space-y-8">
                {specialtyGrants.map((category, index) => (
                  <div key={index} className="bg-[#FAFAF8] rounded-2xl p-10">
                    <h3 className="text-2xl font-bold text-gray-900 mb-6">{category.category}</h3>
                    <div className="grid md:grid-cols-2 gap-6">
                      {category.programs.map((program, idx) => (
                        <div key={idx} className="flex items-start space-x-3">
                          <i className="ri-gift-line text-[#D2691E] text-xl mt-1"></i>
                          <p className="text-sm text-gray-700 leading-relaxed">{program}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-20">
              <h2 className="text-4xl font-bold text-gray-900 mb-12">Application Timeline</h2>
              <div className="grid md:grid-cols-4 gap-6">
                {applicationTimeline.map((phase, index) => (
                  <div key={index} className="bg-[#FAFAF8] rounded-2xl p-8">
                    <div className="w-12 h-12 bg-[#D2691E] rounded-full flex items-center justify-center mb-6 text-white font-bold text-lg">
                      {index + 1}
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{phase.phase}</h3>
                    <p className="text-xs text-[#D2691E] font-semibold mb-4">{phase.duration}</p>
                    <ul className="space-y-2">
                      {phase.tasks.map((task, idx) => (
                        <li key={idx} className="flex items-start space-x-2">
                          <i className="ri-checkbox-circle-line text-[#D2691E] text-base mt-0.5"></i>
                          <span className="text-xs text-gray-700">{task}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-20">
              <h2 className="text-4xl font-bold text-gray-900 mb-12">Tips for Successful Applications</h2>
              <div className="grid md:grid-cols-3 gap-8">
                {successTips.map((tip, index) => (
                  <div key={index} className="bg-[#FAFAF8] rounded-2xl p-8">
                    <div className="w-14 h-14 bg-[#D2691E] rounded-xl flex items-center justify-center mb-6">
                      <i className={`${tip.icon} text-2xl text-white`}></i>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-3">{tip.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{tip.description}</p>
                  </div>
                ))}
              </div>
            </section>

            <div className="bg-[#1B3A3A] rounded-3xl p-12">
              <h2 className="text-4xl font-bold text-white mb-6 text-center">Important Reminders</h2>
              <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
                <div className="bg-white/10 rounded-2xl p-8">
                  <i className="ri-alert-line text-[#D2691E] text-3xl mb-4"></i>
                  <h3 className="font-bold text-xl text-white mb-3">Beware of Scams</h3>
                  <p className="text-sm text-white/90 leading-relaxed">
                    Legitimate grants never require upfront fees. Be cautious of "grant writers" who guarantee awards or charge large advance payments.
                  </p>
                </div>
                <div className="bg-white/10 rounded-2xl p-8">
                  <i className="ri-time-line text-[#D2691E] text-3xl mb-4"></i>
                  <h3 className="font-bold text-xl text-white mb-3">Competitive Process</h3>
                  <p className="text-sm text-white/90 leading-relaxed">
                    Grants are highly competitive. Don't rely solely on grants for funding—pursue multiple financing options simultaneously.
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-12 bg-[#FAFAF8] rounded-3xl p-12 text-center">
              <h3 className="text-4xl font-bold text-gray-900 mb-4">Need Help with Grant Applications?</h3>
              <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
                Our team can guide you through the grant application process and help identify opportunities for your El Paso business.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center space-x-3 bg-[#D2691E] text-white px-8 py-4 rounded-full font-semibold hover:bg-[#B8571A] transition-all shadow-lg cursor-pointer whitespace-nowrap"
              >
                <span>Contact Us</span>
                <i className="ri-arrow-right-line text-xl"></i>
              </Link>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}